import api from './api';
import type { ExameClinicoItem } from './exames.service';

export interface EstadoItem {
  uf: string;
  nome: string;
}

export interface CidadeItem {
  nome: string;
  cidade?: string; // Para compatibilidade
}

export interface BairroItem {
  nome: string;
}

export interface ClinicaHorarioItem {
  dia: number;
  tipo?: string | null;
  de: string | null;
  ate: string | null;
  intervalo_ini?: string | null;
  intervalo_fim?: string | null;
}

export interface ClinicaItem {
  id: number;
  nome: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade: string;
  uf: string;
  cep?: string;
  latitude?: string;
  longitude?: string;
  telefones?: string[];
  emails?: string[];
  horarios?: ClinicaHorarioItem[];
}

class ClinicasService {
  /**
   * Lista estados disponíveis
   */
  async getEstados(): Promise<EstadoItem[]> {
    const response = await api.get<EstadoItem[]>('/clinicas/estados');
    return response.data;
  }

  /**
   * Lista cidades por estado
   */
  async getCidadesByEstado(estado: string): Promise<CidadeItem[]> {
    const response = await api.get<CidadeItem[]>('/clinicas/cidades', {
      params: { estado },
    });
    return response.data;
  }

  /**
   * Lista bairros por cidade e estado
   */
  async getBairrosByCidadeEstado(estado: string, cidade: string): Promise<BairroItem[]> {
    const response = await api.get<BairroItem[]>('/clinicas/bairros', {
      params: { estado, cidade },
    });
    return response.data;
  }

  /**
   * Lista clínicas com filtros de localização
   */
  async getClinicasFiltradas(filters: {
    estado?: string;
    cidade?: string;
    bairro?: string;
    limit?: number;
    online?: boolean;
  } = {}): Promise<ClinicaItem[]> {
    const params: Record<string, string | number | boolean | undefined> = { ...filters };
    if (params.online !== undefined) {
      params.online = params.online ? 'true' : 'false';
    }
    const response = await api.get<ClinicaItem[]>('/clinicas', {
      params,
    });
    return response.data;
  }

  /**
   * Busca uma clínica específica por ID
   */
  async getClinicaById(id: number): Promise<ClinicaItem> {
    const response = await api.get<ClinicaItem>(`/clinicas/${id}`);
    return response.data;
  }

  /**
   * Busca as próximas datas disponíveis para agendamento em uma clínica
   * @param clinicaId - ID da clínica
   * @param limit - Limite de datas a retornar (padrão: 60)
   * @param online - Se true, busca apenas datas com horários online
   */
  async getProximasDatasDisponiveis(
    clinicaId: number,
    limit: number = 60,
    online?: boolean,
    exameId?: number,
    exameClinicoIds?: number[],
  ): Promise<string[]> {
    const params: Record<string, string | number | boolean | undefined> = { clinicaId, limit };
    if (online !== undefined) {
      params.online = online ? 'true' : 'false';
    }
    if (exameId) {
      params.exame_id = exameId;
    }
    if (exameClinicoIds && exameClinicoIds.length > 0) {
      params.exame_clinico_ids = exameClinicoIds.join(',');
    }
    const response = await api.get<string[]>('/clinicas/datas-disponiveis', {
      params,
    });
    return response.data;
  }

  /**
   * Busca todos os exames clínicos disponíveis em uma clínica
   * Retorna exames com obrigatorio=false (será definido pela variação no frontend)
   */
  async getExamesClinicosClinica(clinicaId: number): Promise<ExameClinicoItem[]> {
    const response = await api.get<ExameClinicoItem[]>('/clinicas/exames-clinicos', {
      params: { clinicaId },
    });
    // Garantir que todos os exames tenham os campos necessários
    return response.data.map(exame => ({
      ...exame,
      obrigatorio: exame.obrigatorio || false,
      variacao_exame_clinico_id: exame.variacao_exame_clinico_id || 0,
    }));
  }
}

export const clinicasService = new ClinicasService();

